import './styles.scss';
import React, {Dispatch, useEffect, useRef, useState} from "react";


/* Types, Constants, Utils */
import * as Constants from "../../../constants";
import {TQueryItem} from "../../../constants";
import SearchUtils from "../../../utils/search-utils";

/* Bootstrap */
import {Container, Row, InputGroup, Form} from "react-bootstrap";

/* redux */
import {resetFilters, setCurrentPage, setIndustry, setQuery, suspendSearch} from "../../../redux/slices/search-slice";
import {AnyAction} from "@reduxjs/toolkit";
import {useDispatch, useSelector} from "react-redux";
import {ISearchState} from "../../../types/redux/search";
import {RootState} from "../../../redux";

/* Hooks */
import useConvertToAdvSearch from "../hooks/useConvertToAdvSearch";

/* Components */
import SearchHistoryDropdown from "./search-history-dropdown";
import SearchSave from "./search-save";
import SearchButton from "./search-button";
import {ISavedState} from "../../../types/redux/saved";

import ClearSearchField from "./search-clear";
import SearchIndustryDropdown from "./search-industry-dropdown";
import {NavigateFunction} from "react-router/dist/lib/hooks";
import {useNavigate} from "react-router-dom";
import useInsertDBFields from "../hooks/useInsertDBFields";
import {clearResults} from "../../../redux/slices/result-slice";

interface IProps {
  minimal: boolean;
}

const SearchInput = (props: IProps):JSX.Element => {
  const {
    minimal, /* home screen search field minimal ui */
  } = props;

  /* redux */
  const dispatch:Dispatch<AnyAction> = useDispatch();
  const searchState: ISearchState = useSelector<RootState, ISearchState>(state => state.search);
  const savedState: ISavedState = useSelector<RootState, ISavedState>(state => state.saved);

  /* state */
  const [input, setInput] = useState<string>('');
  const [fieldsSelect, setFieldsSelect] = useState<string>('');
  const [submittedQuery, setSubmittedQuery] = useState<boolean>(false);
  const [industryDropdown, setIndustryDropdown] = useState<string>(searchState.industry);

  /* hooks */
  const navigate: NavigateFunction = useNavigate();
  const insertDBFields = useInsertDBFields()
  const convertToAdvSearch = useConvertToAdvSearch();

  /* refs */
  const inputRef:React.MutableRefObject<any> = useRef(null);
  const inputSelectRef:React.MutableRefObject<any> = useRef(null);

  useEffect(()=>{
    if ((searchState.query.length > 0) && !SearchUtils.isDefaultQuery(searchState.query) && (savedState.history.length > 0)) {
      setSubmittedQuery(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedState.history, searchState.query])

  useEffect(()=>{
    setIndustryDropdown(searchState.industry);
  }, [searchState.industry]);

  const clearField = (): void => {
    setInput('')
    setFieldsSelect('')
  }

  useEffect(()=>{
    clearField();
    if (minimal) {
      setInput('')
    } else {
      /* toggled to advanced search (advSearch) */
      const input: string = SearchUtils.convertSearchQuery(searchState.query);
      if (input !== Constants.DEFAULT_SEARCH.term) {
        handleFieldInput(input)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState.query])

  useEffect(()=> {
    if (!(inputRef || inputRef?.current || inputSelectRef || inputSelectRef?.current)) return;
      const inputScrollListener = (e:any): void => {

      setTimeout(()=> {
        if (inputSelectRef.current && inputRef && inputRef.current && inputRef.current.scrollLeft) {
          inputSelectRef.current.scrollLeft  = inputRef.current.scrollLeft;
        }
      })
    }

    inputRef.current.removeEventListener('scroll', inputScrollListener);
    inputRef.current.addEventListener('scroll', inputScrollListener);
    inputSelectRef.current.style.width = inputRef.current.clientWidth + 'px'; // 33 - account for padding on input

    const _inputRef:any = inputRef;
    return () => {
      if (_inputRef && _inputRef.current) {
        _inputRef.current.removeEventListener('scroll', inputScrollListener);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[inputSelectRef?.current,  inputRef?.current?.clientWidth])

  const syncInputSelect = ()=>{
    setTimeout(()=> {
      inputSelectRef.current.style.width = inputRef.current.clientWidth - 32 + 'px'; // 33 - account for padding on input
      inputSelectRef.current.scrollLeft = inputRef.current.scrollLeft;
    })
  }

  const handleSubmit = (e:React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();

    let term: string = input.trim().replace(/[“”‘’']/g, '"');
    if (term.length === 0) {
      term = Constants.EMPTY_SEARCH;
    }

    const queryItem: TQueryItem = {...Constants.DEFAULT_SEARCH};
    queryItem.term = term;
    const query: TQueryItem[] = convertToAdvSearch.convertQuery([queryItem], searchState.db_set);
    dispatch(setQuery({
      query: query,
      recent_query: true
    }));

    dispatch(setIndustry(industryDropdown));
    dispatch(suspendSearch(false));
    dispatch(setCurrentPage(1))

    if (JSON.stringify(query) !== JSON.stringify(searchState.query)) {
      dispatch(clearResults());
      dispatch(resetFilters());
    }

    if (minimal) {
      navigate('/' + industryDropdown + '/' + Constants.DOCUMENTS + '/?q='+ SearchUtils.searchQueryToString(query) + '&db-set=documents&sort=relevance&pg=1&npp=20');
    }
  }

  const hasQuery = ():boolean => {
    return input.trim().length !== 0;
  }

  const placeholder = ():string =>{
    switch (searchState.db_set) {
      case Constants.DOCUMENTS:
        return Constants.PLACEHOLDER_DOCUMENTS;

      case Constants.BIBLIOGRAPHY:
        return Constants.PLACEHOLDER_BIBLIOGRAPHY;

      default:
        return Constants.PLACEHOLDER_DOCUMENTS
    }
  }

  const handleFieldInput = (input:string): void =>{
    const  {
      str,
      fs
    } = insertDBFields.insertField(input);

    setFieldsSelect(fs)
    setInput(str);
  }

  return (
    <div className={'search-input w-100'}>
      {/*
      *   MOBILE
      */}
      <Container className={'d-lg-none p-0'}>
        <InputGroup className={'bg-white rounded border'}>

          {minimal &&
            <SearchIndustryDropdown
              industry={industryDropdown}
              setIndustry={setIndustryDropdown}
            />}

          {/* Search input field mobile */}
          <div ref={inputSelectRef} className={'sel-container sel position-absolute text-nowrap'} dangerouslySetInnerHTML={{__html:fieldsSelect}}/>
          <Form.Control
            id={'search-input'}
            className={'search-input-field border-0 bg-transparent rounded'}
            placeholder={placeholder()}
            aria-label={placeholder()}
            aria-describedby={'search-input'}
            value={input}
            ref={inputRef}

            onChange={e=>{
              setSubmittedQuery(false);
              handleFieldInput(e.target.value);
              syncInputSelect();
            }}

            onKeyDown={e=>{
              if (e.code === Constants.ENTER) {
                setTimeout(()=>{
                  /* timeout allow for state to update and include last input */
                  handleSubmit(e)
                })
              }
            }}
          />

          {/* Clear input button */}
          {hasQuery() &&
            <ClearSearchField clearField={clearField}/>}

          <div className={'vertical-hr'}/>

          {/* Save Search */}
          {(!minimal) && submittedQuery &&
            <SearchSave/>
          }

          {/* Recent searches dropdown */}
          {(!minimal) &&
            <SearchHistoryDropdown/>
          }

          {/* Search button */}
          <SearchButton
            disabled={!hasQuery()}
            handleSubmit={handleSubmit}/>
        </InputGroup>
      </Container>

      {/*
      *   DESKTOP
      */}
      <Container className={'d-none d-lg-block p-0 bg-white rounded shadow-sm'}>
        <Row className={'m-0 rounded'}>
          <Container className={'p-0 rounded'}>
            <InputGroup className={'input-group border border-start-0 rounded-end'}>
              {minimal &&
                <SearchIndustryDropdown
                  industry={industryDropdown}
                  setIndustry={setIndustryDropdown}
                />}

              {/* Search input field desktop */}
              <div ref={inputSelectRef}
                   className={'sel-container sel position-absolute text-nowrap overflow-hidden'}
                   dangerouslySetInnerHTML={{__html:fieldsSelect}
              }/>
              <Form.Control
                id={'search-input'}
                className={'search-input-field border-0 bg-transparent'}
                placeholder={placeholder()}
                aria-label={placeholder()}
                aria-describedby={'search-input'}
                value={input}
                ref={inputRef}

                onChange={e=>{
                  setSubmittedQuery(false);
                  handleFieldInput(e.target.value);
                  syncInputSelect();
                }}

                onKeyDown={e=>{
                  if (e.code === Constants.ENTER) {
                    setTimeout(()=>{
                      handleSubmit(e)
                    }, 100)
                  }
                }}
              />

              {/* Clear input button */}
              {hasQuery() &&
                <ClearSearchField clearField={clearField}/>}

              <div className={'vertical-hr'}/>

              {/* Save Search */}
              {(!minimal) && submittedQuery &&
                <SearchSave/>
              }

              {/* Recent searches dropdown */}
              {(!minimal) && (searchState.db_set === Constants.DOCUMENTS) &&
                <SearchHistoryDropdown/>
              }

              {/* Search button */}
              <SearchButton
                disabled={!hasQuery()}
                handleSubmit={handleSubmit}/>

            </InputGroup>
          </Container>
        </Row>
      </Container>
    </div>
)}

export default SearchInput;
